<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { emptyGraph } from './composables/useWorkflowGraph';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();

const showAddPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedWorkflow = ref({});
const newWorkflowName = ref('');
const newWorkflowEntityType = ref('ticket');

const records = computed(() => getters['workflows/getWorkflows'].value);
const uiFlags = computed(() => getters['workflows/getUIFlags'].value);

// The selectable entity types come straight from the registered adapter capabilities, so a newly
// registered adapter (e.g. tramite) shows up here automatically without touching this component.
const entityTypes = computed(() =>
  (getters['workflows/getCapabilities'].value || []).map(
    capability => capability.entity_type
  )
);

const entityTypeLabel = entityType =>
  t(`WORKFLOWS.ENTITY_TYPES.${entityType}`, entityType);

onMounted(() => {
  store.dispatch('workflows/get');
  store.dispatch('workflows/fetchCapabilities');
});

const openEditor = workflow => {
  router.push({
    name: 'workflows_edit',
    params: { workflowId: workflow.id },
  });
};

const createWorkflow = async () => {
  if (!newWorkflowName.value) return;
  try {
    const workflow = await store.dispatch('workflows/create', {
      name: newWorkflowName.value,
      entity_type: newWorkflowEntityType.value,
      draft_graph: emptyGraph(),
    });
    useAlert(t('WORKFLOWS.ADD.API_SUCCESS'));
    showAddPopup.value = false;
    newWorkflowName.value = '';
    newWorkflowEntityType.value = 'ticket';
    openEditor(workflow);
  } catch (error) {
    useAlert(t('WORKFLOWS.ADD.API_ERROR'));
  }
};

const openDeletePopup = workflow => {
  selectedWorkflow.value = workflow;
  showDeleteConfirmationPopup.value = true;
};

const confirmDeletion = async () => {
  showDeleteConfirmationPopup.value = false;
  try {
    await store.dispatch('workflows/delete', selectedWorkflow.value.id);
    useAlert(t('WORKFLOWS.DELETE.API_SUCCESS'));
  } catch (error) {
    useAlert(t('WORKFLOWS.DELETE.API_ERROR'));
  }
};

const statusLabel = workflow =>
  workflow.published
    ? t('WORKFLOWS.LIST.STATUS_PUBLISHED', { version: workflow.version })
    : t('WORKFLOWS.LIST.STATUS_DRAFT');
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('WORKFLOWS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('WORKFLOWS.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('WORKFLOWS.HEADER')"
        :description="$t('WORKFLOWS.DESCRIPTION')"
        :link-text="$t('WORKFLOWS.LEARN_MORE')"
        feature-name="workflows"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('WORKFLOWS.ADD.TITLE')"
            @click="showAddPopup = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr v-for="workflow in records" :key="workflow.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="block font-medium text-n-slate-12">
                {{ workflow.name }}
              </span>
              <span class="block text-sm">{{ workflow.description }}</span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-sm">
              {{ statusLabel(workflow) }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-sm">
              {{
                workflow.active
                  ? $t('WORKFLOWS.LIST.ACTIVE')
                  : $t('WORKFLOWS.LIST.INACTIVE')
              }}
            </td>
            <td class="py-4">
              <div class="flex justify-end gap-1">
                <Button
                  v-tooltip.top="$t('WORKFLOWS.LIST.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  @click="openEditor(workflow)"
                />
                <Button
                  v-tooltip.top="$t('WORKFLOWS.LIST.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDeletePopup(workflow)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal
      v-model:show="showAddPopup"
      size="medium"
      :on-close="() => (showAddPopup = false)"
    >
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header :header-title="$t('WORKFLOWS.ADD.TITLE')" />
        <form class="flex flex-col w-full p-8 pt-4 gap-4" @submit.prevent="createWorkflow">
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('WORKFLOWS.ADD.NAME_LABEL') }}
            </span>
            <input
              v-model="newWorkflowName"
              type="text"
              class="w-full"
              :placeholder="$t('WORKFLOWS.ADD.NAME_PLACEHOLDER')"
            />
          </label>
          <label v-if="entityTypes.length" class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('WORKFLOWS.ADD.ENTITY_LABEL') }}
            </span>
            <select v-model="newWorkflowEntityType" class="w-full">
              <option
                v-for="entityType in entityTypes"
                :key="entityType"
                :value="entityType"
              >
                {{ entityTypeLabel(entityType) }}
              </option>
            </select>
          </label>
          <div class="flex justify-end gap-2">
            <Button
              type="button"
              faded
              slate
              :label="$t('WORKFLOWS.ADD.CANCEL')"
              @click="showAddPopup = false"
            />
            <Button
              type="submit"
              :label="$t('WORKFLOWS.ADD.SUBMIT')"
              :disabled="!newWorkflowName"
            />
          </div>
        </form>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="() => (showDeleteConfirmationPopup = false)"
      :on-confirm="confirmDeletion"
      :title="$t('WORKFLOWS.DELETE.CONFIRM.TITLE')"
      :message="$t('WORKFLOWS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('WORKFLOWS.DELETE.CONFIRM.YES')"
      :reject-text="$t('WORKFLOWS.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
